import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import Marquee from 'react-fast-marquee';
import { BsShareFill } from 'react-icons/bs';
import * as APIHelper from '../../../Helper/apiFetch';
import * as nativeEvents from '../../../Helper/native';
import ValentinesFAQ from './ValentinesFAQ';
import ValentinesTAndC from './ValentinesTAndC';
import ScratchCard from './ScratchCard';
import Congratulation from '../../congratulation';
import { formatTimerDisplay, formatNumberWithCommas } from '../utils';
import TopFiveLeaderboard from './TopFiveLeaderboard';
import { ASSET_URLS, BUTTON_SPINNER_STYLE, CAMPAIGN_CONFIG } from '../constants';
const initiaData = require("../../urlEncodeTest.json");

const ValentinesBanner = ({ showBrandsScreen = false, remainingTime = { hours: 0, minutes: 0, seconds: 0, days: 0 }, onShare, cardType, cardPaymentStatus }) => {
  const navigate = useNavigate();
  const [campaignConfig, setCampaignConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);
  const [congratsPopup, setCongratsPopup] = useState(false);
  const [congratsTitle, setCongratsTitle] = useState({ title: 'Congratulations!', msg1: '' });

  useEffect(() => {
    const fetchCampaignConfig = async () => {
      const startTime = Date.now();
      const MIN_LOADING_MS = 600;
      try {
        setLoading(true);
        const currentToken = sessionStorage.getItem('AccessToken');
        
        if (!currentToken) {
          console.log("No access token available");
          setLoading(false);
          return;
        }

        const response = await APIHelper.topFive();
        // const response = initiaData.anniversaryCampaign;
        if (response && !response.error) {
          const data = response.data ?? response;
          setCampaignConfig(data);
          const elapsed = Date.now() - startTime;
          const remaining = Math.max(0, MIN_LOADING_MS - elapsed);
          setTimeout(() => setLoading(false), remaining);
        } else {
          setLoading(false);
          navigate('/error');
        }
      } catch (error) {
        console.error('Error fetching campaign config:', error);
        setLoading(false);
        navigate('/error');
      }
    };

    fetchCampaignConfig();
  }, []);

  const loyaltyBonusData = campaignConfig?.anniversaryCampaign?.loyaltyBonus;
  const cardSpendData = campaignConfig?.anniversaryCampaign?.cardSpend;

  // Loyalty bonus & voucher streak sections visible until 23rd Feb 11:59:59 PM IST, then hidden
  const rewardCutoff = new Date('2026-02-23T23:59:59+05:30'); // Feb 23, 2026 11:59:59 PM IST
  const showUntilCutoff = new Date() <= rewardCutoff;
  const cardPurchaseData = campaignConfig?.anniversaryCampaign?.cardPurchase;
  const voucherStreakData = campaignConfig?.anniversaryCampaign?.voucherStreak;
  const goldPurchaseData = campaignConfig?.anniversaryCampaign?.goldPurchase;

  const getLoyaltyBonusContent = () => {
    if (!loyaltyBonusData) return null;

    const loyaltyBonusAmount = loyaltyBonusData?.loyaltyBonus ?? 0;
    const extraBonusAmount = loyaltyBonusData?.extraBonus ?? 0;
    const loyaltyRedeemed = loyaltyBonusData?.loyaltyRedeemed ?? false;
    const extraRedeemed = loyaltyBonusData?.extraRedeemed ?? false;
    const eligibleForExtraBonus = loyaltyBonusData?.eligibleForExtraBonus ?? false;

    // Helper: claimed content when user has claimed
    const getClaimedContent = () => {
      const claimedAmount = (loyaltyBonusAmount || 0) + (extraBonusAmount || 0);
      return {
        title: claimedAmount > 0 ? `You have claimed your reward of ${claimedAmount} Sats` : "You have claimed your reward",
        showButton: false
      };
    };

    // Case 4: loyaltyBonus <= 0 && !loyaltyRedeemed && extraBonus > 0 && extraRedeemed === true
    // Check this before case 5 since it's a specific subset
    if (loyaltyBonusAmount <= 0 && !loyaltyRedeemed && extraBonusAmount > 0 && extraRedeemed) {
      return {
        title: `You have earned ${extraBonusAmount} Sats`,
        buttonText: "CLAIM NOW",
        showButton: true
      };
    }

    // Case 3: No reward enabled - show "Still you can earn 100 sats" only if eligibleForExtraBonus, else show claimed if applicable
    if (loyaltyBonusAmount <= 0 && extraBonusAmount <= 0) {
      if (eligibleForExtraBonus) {
        return {
          title: "Still you can earn 100 sats by spending",
          buttonText: "SPEND NOW",
          showButton: true
        };
      }
      if (loyaltyRedeemed || extraRedeemed) {
        return getClaimedContent();
      }
      return null;
    }

    // Case 5: Both redeemed OR (loyaltyRedeemed false and extraRedeemed true)
    if ((loyaltyRedeemed && extraRedeemed) || (!loyaltyRedeemed && extraRedeemed)) {
      return getClaimedContent();
    }

    // Case 2: extraBonus > 0 && !extraRedeemed -> show total (loyaltyBonus + extraBonus) if both available, otherwise just extraBonus
    if (extraBonusAmount > 0 && !extraRedeemed) {
      // If both loyaltyBonus and extraBonus are available and not redeemed, show total
      if (loyaltyBonusAmount > 0 && !loyaltyRedeemed) {
        const total = loyaltyBonusAmount + extraBonusAmount;
        return {
          title: `Claim your ${total} Sats Bonus!`,
          buttonText: "CLAIM NOW",
          showButton: true
        };
      }
      // If only extraBonus is available, show just extraBonus
      return {
        title: `Claim your ${extraBonusAmount} sats`,
        buttonText: "CLAIM NOW",
        showButton: true
      };
    }

    // Case 1: loyaltyBonus > 0 && !loyaltyRedeemed (only if extraBonus doesn't apply)
    if (loyaltyBonusAmount > 0 && !loyaltyRedeemed) {
      return {
        title: `Claim your ${loyaltyBonusAmount} Sats Loyalty Bonus!`,
        buttonText: "CLAIM NOW",
        showButton: true
      };
    }

    // Case 6: loyaltyRedeemed true, extraRedeemed false, extraBonus 0 -> user claimed loyalty but can still earn extra
    if (loyaltyRedeemed && !extraRedeemed && extraBonusAmount === 0) {
      if (eligibleForExtraBonus) {
        return {
          title: "Still you can earn 100 sats more",
          buttonText: "SPEND NOW",
          showButton: true
        };
      }
      return getClaimedContent();
    }

    return null;
  };

  const getCardSpendContent = () => {
    if (!cardSpendData) return null;
    const cardIssued = String(campaignConfig?.cardIssued || cardType || '').toUpperCase();
    
    if (cardIssued === 'INTRO') {
      return {
        title: "Get 5% back on Fashion, Food & Travel spends",
        buttonText: "UPGRADE NOW",
        showButton: true,
        onButtonClick: () => campaignConfig?.cardPaymentStatus ? nativeEvents.upgradeCard() : nativeEvents.purchaseCard()
      };
    }
    if (cardIssued === 'NOCARD') {
      return {
        title: "Get 5% back on Fashion, Food & Travel spends",
        buttonText: "BUY NOW",
        showButton: true,
        onButtonClick: () => nativeEvents.purchaseCard()
      };
    }
    return {
      title: "Get 5% back on Fashion, Food & Travel spends",
      buttonText: "SPEND NOW",
      showButton: true,
      onButtonClick: () => nativeEvents.loadCard()
    };
  };

  const getCardPurchaseContent = () => {
    if (!cardPurchaseData) return null;
    const cardIssued = String(cardPurchaseData?.cardIssued || cardType || '').toUpperCase();
    if (cardIssued === 'INTRO') {
      return {
        title: "Get Elite Card at ₹299",
        buttonText: "UPGRADE NOW",
        showButton: true,
        onButtonClick: () => nativeEvents.upgradeCard()
      };
    }
    if (cardIssued === 'NOCARD') {
      return {
        title: "Get Elite Card at ₹499",
        buttonText: "BUY NOW",
        showButton: true,
        onButtonClick: () => nativeEvents.purchaseCard()
      };
    }
    return {
      title: "Card Purchase Reward",
      showButton: false
    };
  };

  const getVoucherStreakContent = () => {
    if (!voucherStreakData) return null;
    const voucherBonus = voucherStreakData?.voucherBonus ?? 0;
    const voucherBonusRedeemed = voucherStreakData?.voucherBonusRedeemed || false;
    const txnsCount = voucherStreakData?.txns?.length ?? 0;
    const remaining = Math.max(0, 5 - txnsCount);
    const buyMoreTitle = remaining > 0
      ? `Buy ${remaining} more voucher${remaining === 1 ? '' : 's'} and earn 500 Sats instantly`
      : "Buy 5 vouchers today and earn 500 Sats instantly";
    
    if (voucherBonus > 0 && !voucherBonusRedeemed) {
      return {
        title: `Claim your ${voucherBonus} Sats Voucher Streak Bonus!`,
        buttonText: "CLAIM NOW",
        showButton: true,
        onButtonClick: () => {
          const bonus = voucherStreakData?.voucherBonus || 0;
          if (bonus > 0) handleClaim('voucherStreak', bonus);
        }
      };
    }
    // voucherBonus present and redeemed - show claimed message
    if (voucherBonus > 0 && voucherBonusRedeemed) {
      return {
        title: `You have claimed ${voucherBonus} Sats`,
        showButton: false
      };
    }
    // voucherBonus is 0 or undefined - show BUY NOW, redirect to offersBrands
    if (voucherBonus === 0 || voucherBonus === undefined) {
      return {
        title: buyMoreTitle,
        buttonText: "BUY NOW",
        showButton: true,
        onButtonClick: () => nativeEvents.offersBrands()
      };
    }
    return {
      title: buyMoreTitle,
      showButton: false
    };
  };

  const getGoldPurchaseContent = () => {
    if (!goldPurchaseData) return null;
    return {
      title: "Buy Gold instantly with 5% off up to ₹50 (min ₹500)",
      showButton: true,
      buttonText: "BUY NOW",
      onButtonClick: () => nativeEvents.lumpsumScreen()
    };
  };

  const handleReveal = (revealType) => {
    APIHelper.updateAnniversaryReveal(revealType)
      .then((response) => {
        if (response && !response.error) {
          const data = response.data ?? response;
          setCampaignConfig((prev) => {
            if (!prev) return data;
            const merged = { ...prev };
            const updated = data?.anniversaryCampaign ?? data;
            if (updated && typeof updated === 'object') {
              const prevCampaign = prev.anniversaryCampaign || {};
              merged.anniversaryCampaign = { ...prevCampaign };
              Object.keys(updated).forEach((key) => {
                const val = updated[key];
                if (val && typeof val === 'object' && !Array.isArray(val)) {
                  merged.anniversaryCampaign[key] = { ...(prevCampaign[key] || {}), ...val };
                } else {
                  merged.anniversaryCampaign[key] = val;
                }
              });
              if (revealType && merged.anniversaryCampaign[revealType]) {
                merged.anniversaryCampaign[revealType] = { ...merged.anniversaryCampaign[revealType], isRevealed: true };
              }
            }
            return merged;
          });
        } else {
          console.error('Error revealing:', response?.error || 'Unknown error');
          navigate('/error');
        }
      })
      .catch((error) => {
        console.error('Error calling reveal API:', error);
        navigate('/error');
      });
  };

  const handleClaim = async (claimType, amount) => {
    try {
      setClaiming(claimType);
      const response = await APIHelper.updateAnniversaryClaim(claimType, amount);
      
      if (response && !response.error) {
        const data = response.data ?? response;
        let rewardAmount = 0;
        if (claimType === 'voucherStreak') {
          const voucherData = data?.anniversaryCampaign?.voucherStreak;
          rewardAmount = voucherData?.voucherBonus ?? data?.rewards ?? data?.sats ?? amount ?? 0;
        } else if (claimType === 'loyaltyBonus') {
          const loyaltyData = data?.anniversaryCampaign?.loyaltyBonus;
          const lb = loyaltyData?.loyaltyBonus ?? 0;
          const eb = loyaltyData?.extraBonus ?? 0;
          rewardAmount = (lb + eb) || (data?.rewards ?? data?.sats ?? amount ?? 0);
        }
        rewardAmount = Number(rewardAmount) || 0;
        setCampaignConfig((prev) => {
          if (!prev) return data;
          const merged = { ...prev };
          const updated = data?.anniversaryCampaign ?? data;
          if (updated && typeof updated === 'object') {
            const prevCampaign = prev.anniversaryCampaign || {};
            merged.anniversaryCampaign = { ...prevCampaign };
            Object.keys(updated).forEach((key) => {
              const val = updated[key];
              if (val && typeof val === 'object' && !Array.isArray(val)) {
                merged.anniversaryCampaign[key] = { ...(prevCampaign[key] || {}), ...val };
              } else {
                merged.anniversaryCampaign[key] = val;
              }
            });
          }
          return merged;
        });
        setCongratsTitle({
          title: 'Congratulations!',
          msg1: claimType === 'voucherStreak'
            ? `You've earned <b>${rewardAmount}</b> Sats for completing 5 vouchers!`
            : `You've earned <b>${rewardAmount}</b> Sats as Loyalty Bonus!`,
        });
        setCongratsPopup(true);
      } else {
        console.error('Error claiming:', response?.error || 'Unknown error');
        navigate('/error');
      }
    } catch (error) {
      console.error('Error calling claim API:', error);
      navigate('/error');
    } finally {
      setClaiming(null);
    }
  };

  const loyaltyContent = getLoyaltyBonusContent();
  const cardSpendContent = getCardSpendContent();
  const cardPurchaseContent = getCardPurchaseContent();
  const voucherStreakContent = getVoucherStreakContent();
  const goldPurchaseContent = getGoldPurchaseContent();

  return (
    <div className="valentines-banner-container">
      <div className="headderMarquee">
        <div className="leftLayoutMonth"></div>
        <Marquee speed={30} gradientColor="none" direction="left">
          <div className="wazirxHeadderTimerTitle">
            Hurry! Campaign is Ending Soon &nbsp;&nbsp;
          </div>
        </Marquee>
        <div className="campTimerContainer" style={{ width: '130px', marginLeft: '0px' }}>
          <img alt=" " className="timerIcon" src={ASSET_URLS.TIMER_ICON} />
          <div className="timeCounter">
            {remainingTime === 0 ? (
              '00:00:00'
            ) : (
              <div style={{ display: 'flex', gap: '2px', marginBottom: '2px' }}>
                {formatTimerDisplay(remainingTime)}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="headerCta">
        <button
          className="shareCta"
          onClick={() => onShare && onShare(initiaData.floatingShareMsg.msg)}
        >
          <BsShareFill size={22} color="#4B2382" />
        </button>
      </div>
      <div className="valentines-subtitle">
          5 Years Of GoSats!
      </div>
      <div className="valentine-brands-subtitle">
          Celebrate with <span className="valentines-highlight-five"> Exclusive Benefits</span>
      </div>
      <div className="valentines-image">
        <img alt=" " className="valentines-image-img" src="https://assets.gosats.io/notification/2026/2/1771300400746" />
      </div>
      <div className="valentines-rewards-container">
        <div className="valentines-image-arrow-container">
          <span><img alt=" " className="valentines-image-arrow" src="https://assets.gosats.io/notification/2026/2/1771243421455" /></span>
          <span className="valentines-highlight">Exclusive Offers</span>
          <span><img alt=" " className="valentines-image-arrow" src="https://assets.gosats.io/notification/2026/2/1771243421094" /></span>
        </div>
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', marginTop: '20px', width: '100%' }}>
            {[...Array(3)].map((_, index) => (
              <div
                key={index}
                className="valentines-shimmer-effect"
                style={{
                  height: '220px',
                  width: '100%',
                  maxWidth: '286px',
                  minHeight: '220px',
                }}
              />
            ))}
          </div>
        ) : (
        <>
        {/* Loyalty Bonus Section */}
        {campaignConfig && loyaltyBonusData && showUntilCutoff && (
          <div className="reward-section" style={{ textAlign: 'center', marginBottom: '20px', position: 'relative' }}>
            {loyaltyBonusData.isRevealed ? (
              <div style={{ padding: '16px 41px 0px 41px', height: 220, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <div className="reward-title">Loyalty Bonus</div>
                {loyaltyContent ? (
                  <div className="valentines-title">{loyaltyContent.title}</div>
                ) : (
                  <div className="valentines-title" style={{ fontSize: '14px' }}>Reward details</div>
                )}
                <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    {loyaltyContent?.showButton && (
                      <button 
                        className="valentines-ctaButton"
                        onClick={() => {
                          if (loyaltyContent.buttonText === 'SPEND NOW') {
                            nativeEvents.loadCard();
                          } else {
                            const loyaltyBonusAmount = loyaltyBonusData?.loyaltyBonus || 0;
                            const extraBonusAmount = loyaltyBonusData?.extraBonus || 0;
                            const loyaltyRedeemed = loyaltyBonusData?.loyaltyRedeemed || false;
                            const extraRedeemed = loyaltyBonusData?.extraRedeemed || false;
                            let amount = 0;
                            if (extraBonusAmount > 0 && !extraRedeemed) {
                              if (loyaltyBonusAmount > 0 && !loyaltyRedeemed) {
                                amount = loyaltyBonusAmount + extraBonusAmount;
                              } else {
                                amount = extraBonusAmount;
                              }
                            } else if (loyaltyBonusAmount > 0 && !loyaltyRedeemed) {
                              amount = loyaltyBonusAmount;
                            }
                            if (amount > 0) handleClaim('loyaltyBonus', amount);
                          }
                        }}
                        disabled={claiming === 'loyaltyBonus'}
                      >
                        {claiming === 'loyaltyBonus' ? (
                          <div style={{ ...BUTTON_SPINNER_STYLE, width: '16px', height: '16px', border: '2px solid rgba(0, 0, 0, 0.2)', borderTopColor: '#000000' }} />
                        ) : loyaltyContent.buttonText}
                      </button>
                    )}
                    <img src={ASSET_URLS.CARD_IMAGE} alt="" style={{ width: '100%', maxWidth: 200, marginBottom: 0, objectFit: 'contain' }} />
                  </div>
                </div>
              </div>
            ) : (
              <ScratchCard
                overlayImage={ASSET_URLS.SCRATCH_OVERLAY}
                onReveal={() => handleReveal('loyaltyBonus')}
                height={220}
              >
                <div className="reward-title">Loyalty Bonus</div>
                {loyaltyContent ? (
                  <div className="valentines-title">{loyaltyContent.title}</div>
                ) : (
                  <div className="valentines-title" style={{ fontSize: '14px' }}>Reward details</div>
                )}
                <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    {loyaltyContent?.showButton && (
                      <button 
                        className="valentines-ctaButton"
                        onClick={() => {
                          if (loyaltyContent.buttonText === 'SPEND NOW') {
                            nativeEvents.loadCard();
                          } else {
                            const loyaltyBonusAmount = loyaltyBonusData?.loyaltyBonus || 0;
                            const extraBonusAmount = loyaltyBonusData?.extraBonus || 0;
                            const loyaltyRedeemed = loyaltyBonusData?.loyaltyRedeemed || false;
                            const extraRedeemed = loyaltyBonusData?.extraRedeemed || false;
                            let amount = 0;
                            if (extraBonusAmount > 0 && !extraRedeemed) {
                              if (loyaltyBonusAmount > 0 && !loyaltyRedeemed) {
                                amount = loyaltyBonusAmount + extraBonusAmount;
                              } else {
                                amount = extraBonusAmount;
                              }
                            } else if (loyaltyBonusAmount > 0 && !loyaltyRedeemed) {
                              amount = loyaltyBonusAmount;
                            }
                            if (amount > 0) handleClaim('loyaltyBonus', amount);
                          }
                        }}
                        disabled={claiming === 'loyaltyBonus'}
                      >
                        {claiming === 'loyaltyBonus' ? (
                          <div style={{ ...BUTTON_SPINNER_STYLE, width: '16px', height: '16px', border: '2px solid rgba(0, 0, 0, 0.2)', borderTopColor: '#000000' }} />
                        ) : loyaltyContent.buttonText}
                      </button>
                    )}
                    <img src={ASSET_URLS.CARD_IMAGE} alt="" style={{ width: '100%', maxWidth: 200, marginBottom: 0, objectFit: 'contain' }} />
                  </div>
                </div>
              </ScratchCard>
            )}
          </div>
        )}

        {/* Card Spend Section */}
        {campaignConfig && cardSpendData && (
          <div className="reward-section" style={{ textAlign: 'center', marginBottom: '20px', position: 'relative' }}>
            {cardSpendData.isRevealed ? (
              <div style={{ padding: '16px 41px 0px 41px', height: 220, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <div className="reward-title">5% on Elite Card Spend</div>
                {cardSpendContent ? (
                  <div className="valentines-title">{cardSpendContent.title}</div>
                ) : (
                  <div className="valentines-title" style={{ fontSize: '14px' }}>Reward details</div>
                )}
                <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    {cardSpendContent?.showButton && (
                      <button className="valentines-ctaButton" onClick={cardSpendContent.onButtonClick}>{cardSpendContent.buttonText}</button>
                    )}
                    <img src={ASSET_URLS.CARD_SPEND_IMAGE} alt="" style={{ width: '100%', maxWidth: 200, marginBottom: 0, objectFit: 'contain' }} />
                  </div>
                </div>
              </div>
            ) : (
              <ScratchCard overlayImage={ASSET_URLS.SCRATCH_OVERLAY} onReveal={() => handleReveal('cardSpend')} height={220}>
                <div className="reward-title">Card Spend</div>
                {cardSpendContent ? (
                  <div className="valentines-title">{cardSpendContent.title}</div>
                ) : (
                  <div className="valentines-title" style={{ fontSize: '14px' }}>Reward details</div>
                )}
                <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    {cardSpendContent?.showButton && (
                      <button className="valentines-ctaButton" onClick={cardSpendContent.onButtonClick}>{cardSpendContent.buttonText}</button>
                    )}
                    <img src={ASSET_URLS.CARD_SPEND_IMAGE} alt="" style={{ width: '100%', maxWidth: 200, marginBottom: 0, objectFit: 'contain' }} />
                  </div>
                </div>
              </ScratchCard>
            )}
          </div>
        )}

        {/* Card Purchase Section */}
        {campaignConfig && cardPurchaseData && (
          <div className="reward-section" style={{ textAlign: 'center', marginBottom: '20px', position: 'relative' }}>
            {cardPurchaseData.isRevealed ? (
              <div style={{ padding: '16px 41px 0px 41px', height: 220, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <div className="reward-title">Elite at 50% Off</div>
                {cardPurchaseContent ? (
                  <div className="valentines-title">{cardPurchaseContent.title}</div>
                ) : (
                  <div className="valentines-title" style={{ fontSize: '14px' }}>Reward details</div>
                )}
                <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    {cardPurchaseContent?.showButton && (
                      <button className="valentines-ctaButton" onClick={cardPurchaseContent.onButtonClick}>{cardPurchaseContent.buttonText}</button>
                    )}
                    <img src={ASSET_URLS.CARD_PURCHASE_IMAGE} alt="" style={{ width: '100%', maxWidth: 200, marginBottom: 0, objectFit: 'contain' }} />
                  </div>
                </div>
              </div>
            ) : (
              <ScratchCard overlayImage={ASSET_URLS.SCRATCH_OVERLAY} onReveal={() => handleReveal('cardPurchase')} height={220}>
                <div className="reward-title">Card Purchase</div>
                {cardPurchaseContent ? (
                  <div className="valentines-title">{cardPurchaseContent.title}</div>
                ) : (
                  <div className="valentines-title" style={{ fontSize: '14px' }}>Reward details</div>
                )}
                <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    {cardPurchaseContent?.showButton && (
                      <button className="valentines-ctaButton" onClick={cardPurchaseContent.onButtonClick}>{cardPurchaseContent.buttonText}</button>
                    )}
                    <img src={ASSET_URLS.CARD_PURCHASE_IMAGE} alt="" style={{ width: '100%', maxWidth: 200, marginBottom: 0, objectFit: 'contain' }} />
                  </div>
                </div>
              </ScratchCard>
            )}
          </div>
        )}

        {/* Voucher Streak Section */}
        {campaignConfig && voucherStreakData && showUntilCutoff && (() => {
          const txnsLength = voucherStreakData?.txns?.length ?? 0;
          const vouchersPurchased = Math.min(txnsLength, 5);
          const showProgress = txnsLength >= 1;
          const VoucherProgress = () => (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', marginTop: '16px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      backgroundColor: i <= vouchersPurchased ? '#FFD700' : 'rgba(128, 105, 0, 0.3)',
                      border: `2px solid ${i <= vouchersPurchased ? '#E6B800' : 'rgba(128, 105, 0, 0.4)'}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: i <= vouchersPurchased ? '#1a1a1a' : 'rgba(255, 255, 255, 0.6)'
                    }}
                  >
                    {i <= vouchersPurchased ? '✓' : ''}
                  </div>
                ))}
              </div>
            </div>
          );
          const VoucherImageContent = () => (
            <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
              <div style={{ position: 'relative', display: 'block', width: '100%' }}>
                {voucherStreakContent?.showButton && (
                  <button 
                    className="valentines-ctaButton"
                    style={{ marginTop: '-30px' }}
                    onClick={voucherStreakContent.onButtonClick}
                    disabled={claiming === 'voucherStreak'}
                  >
                    {claiming === 'voucherStreak' ? (
                      <div style={{ ...BUTTON_SPINNER_STYLE, width: '16px', height: '16px', border: '2px solid rgba(0, 0, 0, 0.2)', borderTopColor: '#000000' }} />
                    ) : voucherStreakContent.buttonText}
                  </button>
                )}
                <img src={ASSET_URLS.VOUCHER_STREAK_IMAGE} alt="" style={{ width: '100%', marginBottom: 0, objectFit: 'contain', marginBottom: '60px' }} />
              </div>
            </div>
          );
          const VoucherImageContentScratch = () => (
            <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
              <div style={{ position: 'relative', display: 'block', width: '100%' }}>
                {voucherStreakContent?.showButton && (
                  <button 
                    className="valentines-ctaButton"
                    style={{ marginTop: '-30px' }}
                    onClick={voucherStreakContent.onButtonClick}
                    disabled={claiming === 'voucherStreak'}
                  >
                    {claiming === 'voucherStreak' ? (
                      <div style={{ ...BUTTON_SPINNER_STYLE, width: '16px', height: '16px', border: '2px solid rgba(0, 0, 0, 0.2)', borderTopColor: '#000000' }} />
                    ) : voucherStreakContent.buttonText}
                  </button>
                )}
                <img src={ASSET_URLS.VOUCHER_STREAK_IMAGE} alt="" style={{ width: '100%', marginBottom: 0, objectFit: 'contain' }} />
              </div>
            </div>
          );
          return (
            <div className="reward-section" style={{ textAlign: 'center', marginBottom: '20px', position: 'relative' }}>
              {voucherStreakData.isRevealed ? (
                <div style={{ padding: showProgress ? '16px 41px 24px 41px' : '16px 0px 0px 0px', minHeight: 220, height: showProgress ? 'auto' : 220, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <div className="reward-title">500 Sats on 5 Vouchers</div>
                  {voucherStreakContent ? (
                    <div className="valentines-title" style={{ margin: showProgress ? '0px 0px 0px 0px' : '0px 41px 0px 41px' }}>{voucherStreakContent.title}</div>
                  ) : (
                    <div className="valentines-title" style={{ fontSize: '14px' }}>Reward details</div>
                  )}
                  {showProgress ? (
                    <>
                      <VoucherProgress />
                      {voucherStreakContent?.showButton && (
                        <button 
                          className="valentines-ctaButton"
                          onClick={voucherStreakContent.onButtonClick}
                          disabled={claiming === 'voucherStreak'}
                          style={{ marginTop: '80px' }}
                        >
                          {claiming === 'voucherStreak' ? (
                            <div style={{ ...BUTTON_SPINNER_STYLE, width: '16px', height: '16px', border: '2px solid rgba(0, 0, 0, 0.2)', borderTopColor: '#000000' }} />
                          ) : voucherStreakContent.buttonText}
                        </button>
                      )}
                    </>
                  ) : (
                    <VoucherImageContent />
                  )}
                </div>
              ) : (
                <ScratchCard overlayImage={ASSET_URLS.SCRATCH_OVERLAY} onReveal={() => handleReveal('voucherStreak')} height={220}>
                  <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', width: '100%' }}>
                    <div className="reward-title">Voucher Streak</div>
                    {voucherStreakContent ? (
                      <div className="valentines-title">{voucherStreakContent.title}</div>
                    ) : (
                      <div className="valentines-title" style={{ fontSize: '14px' }}>Reward details</div>
                    )}
                    {showProgress ? (
                      <>
                        <VoucherProgress />
                        {voucherStreakContent?.showButton && (
                          <button 
                            className="valentines-ctaButton"
                            onClick={voucherStreakContent.onButtonClick}
                            disabled={claiming === 'voucherStreak'}
                            style={{ marginTop: '80px' }}
                          >
                            {claiming === 'voucherStreak' ? (
                              <div style={{ ...BUTTON_SPINNER_STYLE, width: '16px', height: '16px', border: '2px solid rgba(0, 0, 0, 0.2)', borderTopColor: '#000000' }} />
                            ) : voucherStreakContent.buttonText}
                          </button>
                        )}
                      </>
                    ) : (
                      <VoucherImageContentScratch />
                    )}
                  </div>
                </ScratchCard>
              )}
            </div>
          );
        })()}

        {/* Gold Purchase Section */}
        {campaignConfig && goldPurchaseData && (
          <div className="reward-section" style={{ textAlign: 'center', marginBottom: '20px', position: 'relative' }}>
            {goldPurchaseData.isRevealed ? (
              <div style={{ padding: 0, height: 220, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <div className="reward-title" style={{ margin: '20px 41px 0px 41px' }}>5% Off on Gold</div>
                {goldPurchaseContent ? (
                  <div className="valentines-title" style={{ margin: '10px 41px 0px 41px' }}>{goldPurchaseContent.title}</div>
                ) : (
                  <div className="valentines-title" style={{ fontSize: '14px' }}>Reward details</div>
                )}
                <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                  <div style={{ position: 'relative', display: 'block', width: '100%' }}>
                    {goldPurchaseContent?.showButton && (
                      <button className="valentines-ctaButton" style={{ marginTop: '-30px' }} onClick={goldPurchaseContent.onButtonClick}>{goldPurchaseContent.buttonText}</button>
                    )}
                    <img src={ASSET_URLS.GOLD_PURCHASE_IMAGE} alt="" style={{ width: '100%', marginBottom: 0, objectFit: 'contain' }} />
                  </div>
                </div>
              </div>
            ) : (
              <ScratchCard overlayImage={ASSET_URLS.SCRATCH_OVERLAY} onReveal={() => handleReveal('goldPurchase')} height={220}>
                <div className="reward-title">Gold Purchase</div>
                {goldPurchaseContent ? (
                  <div className="valentines-title">{goldPurchaseContent.title}</div>
                ) : (
                  <div className="valentines-title" style={{ fontSize: '14px' }}>Reward details</div>
                )}
                <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                  <div style={{ position: 'relative', display: 'block', width: '100%' }}>
                    {goldPurchaseContent?.showButton && (
                      <button className="valentines-ctaButton" style={{ marginTop: '-30px' }} onClick={goldPurchaseContent.onButtonClick}>{goldPurchaseContent.buttonText}</button>
                    )}
                    <img src={ASSET_URLS.GOLD_PURCHASE_IMAGE} alt="" style={{ width: '100%', marginBottom: 0, objectFit: 'contain' }} />
                  </div>
                </div>
              </ScratchCard>
            )}
          </div>
        )}
        </>
        )}
      </div>
      {/* Leaderboard Section - Top Stacker design */}
      {campaignConfig?.topFive && campaignConfig.topFive.length > 0 && (
          <div style={{ marginTop: '50px', marginBottom: '20px' }}>
            <div className="airdropContainer">
              <img src="https://assets.gosats.io/notification/2026/2/1771322069292" style={{ maxWidth: '50px', height: 'auto' }} alt="" />
              <span className="airdropTitle">HIGHEST STACKER KAUN</span>
              <img src="https://assets.gosats.io/notification/2026/2/1771322069677" style={{ maxWidth: '50px', height: 'auto' }} alt="" />
            </div>
            <div className="cardSpndContainerTwo" style={{ marginTop: '20px' }}>
              
              <span style={{ display: 'flex', justifyContent: 'center' }}>
                <div className="weekDates">{campaignConfig?.currentWeek?.dateString || CAMPAIGN_CONFIG.CAMPAIGN_PERIOD}</div>
              </span>
              <span style={{ display: 'flex', justifyContent: 'center' }}>
                <div className="airdropSubTitle">
                  Top 50 earners in this campaign period will unlock bonus rewards from a {CAMPAIGN_CONFIG.LEADERBOARD_POOL} pool!
                </div>
              </span>
              {cardType && cardType !== 'ELITE' && (
                <div className="highestStackerContainerWrapper">
                  <div className="highestStackerContainer">
                    <div>
                      <img src={ASSET_URLS.DECORATION_11} style={{ maxWidth: '42px', height: 'auto' }} alt="" />
                    </div>
                    <div className="highestStackerTitle">
                      Upgrade to Elite to participate in the Highest Stacker Kaun
                    </div>
                  </div>
                  <div>
                    <button className="highestStackerButton" onClick={() => {
                      if (cardType === 'INTRO' && cardPaymentStatus) nativeEvents.upgradeCard();
                      else if (cardType === 'INTRO' && !cardPaymentStatus) nativeEvents.purchaseCard();
                      else if (cardType === 'NOCARD') nativeEvents.activateCard();
                    }}>
                      {cardType === 'INTRO' ? 'Upgrade Now' : 'Activate Now'}
                    </button>
                  </div>
                </div>
              )}
              <div>
                <div>
                  {(campaignConfig?.totalRewardsInINR > 0 || campaignConfig?.totalTxnAmount > 0) ? (
                    <div className="d-flex postion-container justify-content-between">
                      <div>
                        <span className="sats">#{campaignConfig?.userRank ?? '-'}</span>
                      </div>
                      <div className="d-flex gap-1 align-content-center" style={{ alignItems: 'center' }}>
                        <span className="position-title">Your Earned Rewards:</span>
                        <span className="sats">
                          ₹ {formatNumberWithCommas(Math.round(campaignConfig?.totalRewardsInINR || 0))}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="d-flex postion-container justify-content-between" style={{ textAlign: 'left', fontSize: '14px', fontWeight: '400', color: '#000000' }}>
                      <span>Start spending to enter in the leaderboard</span>
                    </div>
                  )}
                </div>
                {campaignConfig.topFive.map((obj, index) => (
                  <TopFiveLeaderboard
                    key={obj.userId || index}
                    userData={obj}
                    rank={index + 1}
                    currentUserId={campaignConfig?.userId}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
        <div className="footerContainer" style={{ marginTop: '0px' }}>
          <div style={{ padding: '0px 0px 50px 0px' }}>
            <div style={{ marginBottom: '28px', marginTop: '40px' }}>
              <ValentinesFAQ />
            </div>
            <ValentinesTAndC />
          </div>
        </div>

      {congratsPopup && (
        <Congratulation
          title={congratsTitle}
          onAction={() => {
            setCongratsPopup(false);
            window.location.href = '/home';
          }}
          onClose={() => {
            setCongratsPopup(false);
            window.location.href = '/home';
          }}
        />
      )}
    </div>
  )
};

export default ValentinesBanner;
